import {Checkout, Customer, IAddressData} from "@connox/typescript-library";
import {googleToInternalAddress} from "./googlePayHelperFunctions";
import {
  ThreeDSecureBillingAddress,
  ThreeDSecureShippingAddress,
  ThreeDSecureVerifyOptions,
} from "braintree-web/three-d-secure";
import {GooglePayDataType} from "../../../cart/CartContextProvider/CartContextProvider";

export class GPayAuthError extends Error {
  constructor(message: string, name?: string) {
    super(message);
    if (!name) {
      this.name = "GPayAuthError";
    } else if (name.endsWith("Error")) {
      this.name = name;
    } else {
      this.name = name + "Error";
    }
  }
}

/**
 * When Google Pay is authorized. Check if all necessary data is present
 * @param paymentData
 * @param customer
 * @param checkout
 */
export const onGoogleAuthorized = async (
  paymentData: google.payments.api.PaymentData,
  customer: Customer,
  checkout: Checkout | undefined,
): Promise<void> => {
  const email = customer.email || paymentData.email;
  if (!paymentData.shippingAddress) {
    throw new GPayAuthError("No shipping address on google pay auTTorize!");
  }
  if (!paymentData.paymentMethodData.info?.billingAddress) {
    throw new GPayAuthError("No billing address on google pay auTTorize!");
  }
  if (!email) {
    throw new GPayAuthError("No email given on google pay auTTorize!");
  }
  if (!checkout) {
    throw new GPayAuthError("No checkout ref on google pay auTTorize!");
  }
  if (!paymentData.shippingAddress.address1 || !paymentData.paymentMethodData.info.billingAddress.address1) {
    throw new GPayAuthError("No Street and No. on google pay auTTorize!");
  }

  let logisticsTypeId: number | undefined;
  if (paymentData.shippingOptionData && Number(paymentData.shippingOptionData.id) !== checkout.logisticsTypeId) {
    logisticsTypeId = Number(paymentData.shippingOptionData.id);
  }

  const delivery: IAddressData = googleToInternalAddress(paymentData.shippingAddress, email, checkout.deliveryAddress);
  const invoice: IAddressData = googleToInternalAddress(
    paymentData.paymentMethodData.info.billingAddress,
    email,
    checkout.invoiceAddress,
  );

  await checkout.put({
    logisticsTypeId,
    address: {
      delivery,
      invoice,
    },
  });
};

// <https://braintree.github.io/braintree-web/current/ThreeDSecure.html#~verifyPayload>
export const getVerifyCardOptions = (gPayData: GooglePayDataType, amount: string): ThreeDSecureVerifyOptions => {
  let billingAddress: ThreeDSecureBillingAddress | undefined;
  const gPayBillingAddress: google.payments.api.Address | undefined =
    gPayData.paymentData.paymentMethodData.info?.billingAddress;
  if (gPayBillingAddress) {
    const nameArray = gPayBillingAddress.name?.split(" ");
    const givenName = nameArray && nameArray.shift();
    const surname = nameArray && nameArray.join(" ");
    billingAddress = {
      givenName,
      surname,
      streetAddress: gPayBillingAddress.address1,
      postalCode: gPayBillingAddress.postalCode,
      locality: gPayBillingAddress.locality,
      countryCodeAlpha2: gPayBillingAddress.countryCode,
      region: gPayBillingAddress.administrativeArea,
      phoneNumber: gPayBillingAddress.phoneNumber,
    };
  }

  let shippingAddress: ThreeDSecureShippingAddress | undefined;
  const gpayShippingAddress: google.payments.api.Address | undefined = gPayData.paymentData.shippingAddress;
  if (gpayShippingAddress) {
    shippingAddress = {
      streetAddress: gpayShippingAddress.address1 ?? "",
      postalCode: gpayShippingAddress.postalCode,
      locality: gpayShippingAddress.locality,
      countryCodeAlpha2: gpayShippingAddress.countryCode,
      region: gpayShippingAddress.administrativeArea,
      extendedAddress: "",
      line3: "",
    };
  }

  const shippingNameArray = gpayShippingAddress?.name?.split(" ");
  const shippingGivenName = shippingNameArray?.shift();
  const shippingSurname = shippingNameArray?.join(" ");

  return {
    nonce: gPayData.tokenData.nonce,
    bin: gPayData.tokenData.details.bin,
    amount,
    email: gPayData.paymentData.email,
    billingAddress,
    additionalInformation: {
      shippingPhone: gpayShippingAddress?.phoneNumber,
      shippingGivenName,
      shippingSurname,
      shippingAddress,
    },
  };
};
